/**
 * Shared formatting helpers for the Activations/Distribution feature.
 *
 * `formatTimestamp` lived in DistributionConfirmation.tsx historically; it was
 * moved here when the Confirmation step was consolidated into the wizard.
 */

export function formatTimestamp(
  timestamp: number | string | Date | null | undefined
): string | null {
  if (timestamp === null || timestamp === undefined || timestamp === '') {
    return null;
  }

  // GraphQL serializes our Activation `startedAt` / `finishedAt` as String
  // (typeDefs uses `String`, not a Date scalar). For mongoose Date fields
  // that means we receive a stringified millisecond Unix timestamp like
  // "1780001732181" — String#toLocaleString just echoes the string back,
  // which is why the raw number rendered. Coerce numeric strings to Number
  // before constructing the Date; leave ISO strings and real numbers alone.
  let raw: number | string | Date = timestamp;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) {
    raw = Number(raw);
  }
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export function formatUserCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M Users`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1).replace(/\.0$/, '')}K Users`;
  }
  return `${count.toLocaleString()} Users`;
}

export function formatDate(date: unknown): string {
  if (!date) return '';
  let d: Date;
  if (typeof date === 'string') {
    d = /^\d+$/.test(date) ? new Date(Number(date)) : new Date(date);
  } else if (typeof date === 'number') {
    d = new Date(date);
  } else if (date instanceof Date) {
    d = date;
  } else {
    return '';
  }
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}
